import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, Star, MessageSquare } from "lucide-react";
import Layout from "../components/layout/Layout";
import ReviewCard from "../components/ui/ReviewCard";
import { useReviews } from "../context/ReviewsContext";
import { useExpert } from "../hooks/useExperts";

export default function ExpertReviewsPage() {
  const { id } = useParams();
  const { expert, loading: expertLoading } = useExpert(id);
  const { getReviewsForExpert } = useReviews();
  const [dbReviews, setDbReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (expert && !loaded) {
      const load = async () => {
        const reviews = await getReviewsForExpert(expert.id);
        setDbReviews(reviews);
        setLoaded(true);
      };
      load();
    }
  }, [expert, loaded, getReviewsForExpert]);

  const displayReviews = loaded ? dbReviews : expert?.reviews || [];

  if (expertLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-lg font-extralight text-neutral-400">Загрузка...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!expert) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-medium text-white mb-4">
              Эксперт не найден
            </h1>
            <Link
              to="/experts"
              className="text-primary-400 hover:text-primary-300 text-lg font-extralight"
            >
              Вернуться к каталогу
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const averageRating =
    displayReviews.length > 0
      ? (
          displayReviews.reduce((sum, r) => sum + r.rating, 0) /
          displayReviews.length
        ).toFixed(1)
      : 0;

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: displayReviews.filter((r) => r.rating === star).length,
    percentage:
      displayReviews.length > 0
        ? (displayReviews.filter((r) => r.rating === star).length /
            displayReviews.length) *
          100
        : 0
  }));

  return (
    <Layout>
      <div id="expert-reviews_page" className="min-h-screen">
        <section className="py-8 lg:py-12 bg-gradient-to-b from-primary-950/30 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to={`/experts/${expert.id}`}
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">
                Назад к профилю {expert.name}
              </span>
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8"
            >
              <div className="flex items-center gap-4">
                <img
                  src={expert.avatar}
                  alt={expert.name}
                  className="w-16 h-16 rounded-md object-cover"
                />
                <div>
                  <h1 className="text-2xl lg:text-3xl font-medium text-white">
                    Отзывы
                  </h1>
                  <p className="text-lg font-extralight text-neutral-400">
                    {expert.name}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-md bg-neutral-800/30 border border-neutral-800/30 mb-8"
            >
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <div className="text-center">
                  <div className="text-5xl font-medium text-white mb-2">
                    {averageRating}
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(Number(averageRating))
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-neutral-700"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-lg font-extralight text-neutral-400">
                    {displayReviews.length} отзывов
                  </p>
                </div>

                <div className="flex-1 w-full space-y-2">
                  {ratingDistribution.map(({ star, count, percentage }) => (
                    <div key={star} className="flex items-center gap-3">
                      <span className="text-lg font-extralight text-neutral-400 w-8 text-right">
                        {star}
                      </span>
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                      <div className="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-yellow-400 transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-lg font-extralight text-neutral-500 w-8">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-8 lg:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {displayReviews.map((review, index) => (
                <motion.div
                  key={review.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                >
                  <ReviewCard review={review} />
                </motion.div>
              ))}
            </div>

            {displayReviews.length === 0 && (
              <div className="text-center py-16">
                <MessageSquare className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                <p className="text-xl font-extralight text-neutral-400">
                  Пока нет отзывов
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
